import { z } from 'zod';
import { EnrollmentType } from './enrollment-type.js';
/**
 * Anonymous enrollment status page (E-SF07) — the shape US-Shop's status page
 * reads from `GET /bff/status/:token`.
 *
 * ── WHY THIS LIVES IN THE CONTRACT ─────────────────────────────────────────
 * It is the one edge between the SPA and the BFF (ADR-0002 §4). The BFF uses it
 * as the route `response` schema (so the boot-time response-schema guard walks
 * it and the handler validates its own output); US-Shop imports it for the typed
 * status fetcher.
 *
 * ── NO PII / NO IDS / NO TENANT (AC2) ──────────────────────────────────────
 * The status TOKEN is the capability and rides in the URL path — it is never
 * echoed on the body. This response declares NO tenantId, NO account/quote id,
 * NO ESIID, and NO PII: the anonymous caller gets the lifecycle state, the
 * scheduled date, and — for a rejection — the reason + whether it is
 * self-serve resolvable, and nothing enumerable. The response-guard forbids
 * tenantId/esiid/ssn/dob/secrets, and this shape is written to satisfy it.
 */
/** ISO calendar date (YYYY-MM-DD). Service dates are Texas (America/Chicago) days. */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'date must be YYYY-MM-DD');
/**
 * The enrollment lifecycle the status page renders.
 *
 *  - PENDING   → enrolled; the switch/move-in has not yet been scheduled to a date.
 *  - SCHEDULED → a service start date is locked in (carried in `serviceStartDate`).
 *  - ACTIVE    → the service start date has been reached; supply is (being) served.
 *  - REJECTED  → the enrollment was rejected/cancelled; `rejection` explains why
 *                and whether the customer can self-serve resolve it (switch-hold).
 */
export const EnrollmentLifecycleState = z.enum(['PENDING', 'SCHEDULED', 'ACTIVE', 'REJECTED']);
/**
 * Why an enrollment is in the REJECTED state.
 *
 *  - SWITCH_HOLD → the incoming switch is held (e.g. a new-occupant / prior-
 *                  balance hold at the premise). SELF-SERVE RESOLVABLE via the
 *                  New Occupant Statement flow (AC1).
 *  - RESCINDED   → cancelled within the 3-business-day switch rescission window.
 *  - CANCELLED   → cancelled for another reason (not self-serve resolvable here).
 *  - OTHER       → a rejection with no customer-actionable next step in the funnel.
 */
export const EnrollmentRejectionReason = z.enum([
    'SWITCH_HOLD',
    'RESCINDED',
    'CANCELLED',
    'OTHER',
]);
/**
 * The rejection detail. `resolvable` is SERVER-AUTHORITATIVE — the FE gates the
 * self-serve resolution flow (New Occupant Statement) on it and never derives it
 * from the reason. Only a switch-hold is resolvable in this funnel; everything
 * else routes the customer to support (copy, FE-side).
 */
export const EnrollmentRejectionSchema = z.object({
    reason: EnrollmentRejectionReason,
    /** Whether the customer can self-serve resolve this rejection (switch-hold → true). */
    resolvable: z.boolean(),
});
/**
 * `GET /bff/status/:token` response — the anonymous status page's whole view.
 *
 * `serviceStartDate` is present for SCHEDULED/ACTIVE (the contract-locked start
 * date, server-authoritative — never re-derived in the browser) and null before
 * one is known. `rescissionDeadline` is the SWITCH 3-business-day deadline
 * (server-computed) or null (move-in / already past). `rejection` is non-null
 * only in the REJECTED state.
 */
export const EnrollmentStatusResponseSchema = z.object({
    state: EnrollmentLifecycleState,
    /** SWITCH vs MOVE_IN — drives the lifecycle copy (switch rescission, move-in start). */
    enrollmentType: EnrollmentType,
    /** The locked service start date (SCHEDULED/ACTIVE). Null until one is known. */
    serviceStartDate: IsoDateSchema.nullable(),
    /** SWITCH only: the 3-business-day right-of-rescission deadline. Null otherwise. */
    rescissionDeadline: IsoDateSchema.nullable(),
    /** Present only in the REJECTED state; null otherwise. */
    rejection: EnrollmentRejectionSchema.nullable(),
    /**
     * Whether the signed contract EXISTS and can be downloaded yet.
     *
     * This is the signal the confirmation screen polls. Since E-SF06 the accept is
     * asynchronous: the signature is durable immediately, the contract is written
     * by the signature worker moments later, and between those two instants there
     * is a real enrollment with no document behind it. A download offered in that
     * window 404s, so the browser has to be told rather than left to guess.
     *
     * ⚠️ DELIBERATELY NOT DERIVED FROM `state` ON THE CLIENT. It happens to be
     * true exactly when the contract exists, which today correlates with
     * SCHEDULED/ACTIVE — but leaning on that coupling turns any future lifecycle
     * change into a silent UI bug, and the two facts are not the same question.
     */
    agreementAvailable: z.boolean(),
});
//# sourceMappingURL=status.js.map